import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useAppUser } from '../auth/AppUserContext';
import type { ServerTask } from '../data/tasks';
import { canConductAudits } from '../data/scope';
import { canStartTemplate } from '../audit/requirements';
import { appliesTo } from '../audit/assignment';
import type { Store } from '../data/types';
import { buildDistrictRows, type StoreRow } from '../data/dashboard';
import { buildCallList } from '../data/callList';
import { CALL_LIST_CSV_HEADER, callListCsvRows, DISTRICT_CSV_HEADER, districtCsvRows } from '../data/dashboardCsv';
import { BoardTable, DistrictTable, GradeHelp, type GradesState } from '../ui/dashboard/DashboardTables';
import { StoreBoard } from '../ui/dashboard/StoreBoard';
import { useDashboardData } from '../ui/dashboard/useDashboardData';
import { limitedAccessLine } from '../ui/accessSummary';
import { CompletedAuditsList } from '../ui/audits/CompletedAuditsList';
import { TaskRow } from '../ui/tasks/TaskRow';
import { CsvButton } from '../ui/CsvButton';
import { roleLabel } from '../ui/roleLabel';
import { OwnerSelect, useOwnerFilter } from '../ui/reports/OwnerFilter';
import { withOwnerHeader } from '../reports/ownerColumn';
import { PagerBar, PagerTop, usePager } from '../ui/Pager';


/**
 * Home = the role dashboard, one layout per role (Home reorganisation, owner
 * rulings 2026-10-25 — the page had grown too many tables):
 * - Executive / Auditor / Admin: the district table (drill into a district
 *   for its stores), then "Call this week" — the 10 stores most behind
 *   across every district, as a store board with a "Why it's here" column.
 * - DM: their store board, then their open tasks, due date first (the most
 *   overdue on top).
 * - Store team: open tasks first, then the store's audit status, then its
 *   recent audits.
 * Audit status is computed LIVE from StoreAuditStatus dates + template
 * windows; grades = src/audit/grades.ts (useDashboardData reads everything).
 */
export function HomePage() {
  const { user } = useAppUser();
  // Shown once: the router state is cleared right away, so a reload or Back
  // to Home doesn't repeat it (QA 2026-10-25).
  const location = useLocation();
  const navigate = useNavigate();
  const [denied] = useState(() => typeof (location.state as { deniedFrom?: unknown } | null)?.deniedFrom === 'string');
  useEffect(() => {
    if (denied) navigate('.', { replace: true, state: null });
  }, [denied, navigate]);

  const { stores, templates, tasks, failed, refreshing, error, rows: allRows, gradesState, districtRows: allDistrictRows, recentAudits } =
    useDashboardData();
  // The Owner filter (staff; 2026-10-28) narrows the boards to one owner's stores. The district table is
  // rebuilt from those stores — a district's grade then means "its stores of that owner".
  const ownerFilter = useOwnerFilter();
  const rows = useMemo(
    () => (allRows === null || !ownerFilter.owner ? allRows : allRows.filter((r) => ownerFilter.apply([r.store]).length > 0)),
    [allRows, ownerFilter],
  );
  // The DM's Open tasks and the board's store picker follow the owner too (QA 2026-10-28).
  const ownerStores = useMemo(() => (stores === null ? null : ownerFilter.apply(stores)), [stores, ownerFilter]);
  const districtRows = useMemo(
    () => (!ownerFilter.owner ? allDistrictRows : rows === null || gradesState !== 'ready' ? null : buildDistrictRows(rows)),
    [ownerFilter.owner, allDistrictRows, rows, gradesState],
  );

  if (!user) return null;
  const layout = user.role === 'StoreTeam' ? 'store' : user.role === 'DM' ? 'dm' : 'portfolio';

  return (
    <div className={layout === 'store' ? undefined : 'dash-page'}>
      {denied && (
        <p className="denied-note" role="status">
          That page isn’t part of your access, so you’re back on Home.
        </p>
      )}
      <div className="page-head">
        <div>
          <h1>Welcome, {user.displayName}</h1>
          <p className="muted" style={{ margin: 0 }}>
            {roleLabel(user.role)}
            {user.role === 'DM' && user.districts.length > 0 && (
              <> · {user.districts.join(', ')}</>
            )}
          </p>
          {limitedAccessLine(user) && (
            <p className="muted access-line" style={{ margin: '0.15rem 0 0' }}>
              {limitedAccessLine(user)}
            </p>
          )}
        </div>
        {(canConductAudits(user.role) ||
          (user.role === 'StoreTeam' &&
            (templates ?? []).some(
              (t) => canStartTemplate('StoreTeam', t.requirements) && (stores ?? []).some((s) => appliesTo(t.assignment, s)),
            ))) && (
          <Link className="btn btn-primary" to="/audits">
            Start an audit
          </Link>
        )}
      </div>

      {error && <p className="error">{error}</p>}
      {rows === null && !error && <p>Loading…</p>}
      {ownerFilter.enabled && layout !== 'store' && allRows !== null && allRows.length > 0 && (
        <div className="list-tools" role="group" aria-label="Filters">
          <OwnerSelect filter={ownerFilter} />
        </div>
      )}

      {rows !== null && rows.length === 0 && ownerFilter.owner !== '' && (
        <p className="muted">{ownerFilter.owner === 'none' ? 'Every one of your stores has an owner.' : `None of your stores belong to ${ownerFilter.label.replace(/^Owner /, '') || 'that owner'}.`}</p>
      )}
      {rows !== null && rows.length === 0 && ownerFilter.owner === '' && (
        <p className="muted">
          No stores are visible to your account yet. An admin may need to add
          stores or assign your district or store.
        </p>
      )}

      {rows !== null && rows.length > 0 && (
        <>
          {/* Store teams: their tasks come first (owner ruling 2026-10-25). */}
          {layout === 'store' && (
            <OpenTasks
              heading="Your open tasks"
              tasks={tasks}
              failed={failed.includes('open tasks')}
              stores={stores ?? []}
              upn={user.upn}
            />
          )}

          <GradeHelp />
          {failed.length > 0 && (
            <p className="attr-warning">
              Grades are unavailable right now — couldn’t load {failed.join(', ')}. Refresh the page to
              try again.
            </p>
          )}
          {refreshing && failed.length === 0 && (
            <p className="attr-warning">Some months are being refreshed — grades may change after a reload.</p>
          )}

          {layout === 'portfolio' && (
            <>
              <section className="dash-section">
                <div className="page-head">
                  <h2 style={{ margin: 0 }}>Districts</h2>
                  {districtRows !== null && (
                    <CsvButton name="districts" header={DISTRICT_CSV_HEADER} rows={() => districtCsvRows(districtRows, true)} />
                  )}
                </div>
                {districtRows === null ? (
                  gradesState === 'unavailable' ? (
                    <p className="muted">The district table needs every input — refresh to try again.</p>
                  ) : (
                    <p>Loading…</p>
                  )
                ) : (
                  <DistrictTable rows={districtRows} />
                )}
              </section>
              <CallThisWeek
                rows={rows}
                gradesState={gradesState}
                single={new Set(rows.map((r) => r.store.district.trim().toLowerCase())).size <= 1}
              />
            </>
          )}

          {layout !== 'portfolio' && (
            <StoreBoard
              rows={rows}
              stores={ownerStores ?? []}
              templates={templates}
              gradesState={gradesState}
              heading={layout === 'store' && rows.length === 1 ? 'Your store' : 'Stores'}
              forStoreTeam={layout === 'store'}
            />
          )}

          {layout === 'dm' && (
            <OpenTasks
              heading="Open tasks"
              tasks={tasks}
              failed={failed.includes('open tasks')}
              stores={ownerStores ?? []}
              upn={user.upn}
            />
          )}

          {layout === 'store' && (
            <section className="dash-section">
              <div className="page-head">
                <h2 style={{ margin: 0 }}>Recently completed audits</h2>
                <Link to="/audits">All audits →</Link>
              </div>
              <CompletedAuditsList items={recentAudits} filterStores={stores ?? []} />
            </section>
          )}
        </>
      )}
    </div>
  );
}

/**
 * "Call this week" (B7; as a store board since the Home reorganisation,
 * owner rulings 2026-10-25): the 10 stores most behind across every
 * district — lateness first, then untouched overdue tasks, then a failing
 * Quality grade (src/data/callList.ts) — with why each is listed. Waits for
 * every input (a ranking built on half the data would reshuffle).
 */
function CallThisWeek({ rows, gradesState, single }: { rows: StoreRow[]; gradesState: GradesState; single: boolean }) {
  // While old months refresh it ranks anyway — the amber note above says
  // grades may change (QA 2026-10-25: a refresh that can't finish left it
  // on "Loading…" for the whole visit).
  const ready = gradesState === 'ready';
  const ownerOf = useOwnerFilter().ownerOf;
  const items = useMemo(() => (ready ? buildCallList(rows, new Date()) : null), [rows, ready]);
  const reasons = useMemo(() => new Map((items ?? []).map((i) => [i.row.store.id, i.reason])), [items]);
  return (
    <section className="dash-section">
      <div className="page-head">
        <h2 style={{ margin: 0 }}>Call this week</h2>
        <span className="page-head-tools">
          {items !== null && items.length > 0 && (
            <CsvButton
              name="call this week"
              header={withOwnerHeader(CALL_LIST_CSV_HEADER, ownerOf)}
              rows={() => callListCsvRows(items, new Date(), true, ownerOf)}
            />
          )}
        </span>
      </div>
      {gradesState === 'unavailable' ? (
        <p className="muted">
          Can’t rank stores right now — some data couldn’t be loaded. Open a district above to see its stores.
        </p>
      ) : items === null ? (
        <p>Loading…</p>
      ) : items.length === 0 ? (
        <p className="muted">No store is behind right now — nothing to chase this week.</p>
      ) : (
        <>
          <p className="muted call-intro">
            The {items.length === 1 ? 'store' : `${items.length} stores`} most behind {single ? 'in your district' : 'across your districts'} — most days
            overdue across all audits first, then overdue tasks nobody has touched, then a failing Quality grade.
          </p>
          <BoardTable
            rows={items.map((i) => i.row)}
            now={new Date()}
            gradesState={gradesState}
            why={(row) => reasons.get(row.store.id) ?? ''}
          />
        </>
      )}
    </section>
  );
}

/**
 * Open tasks, by due date (the most overdue on top; no date last) — the same
 * rule as the Tasks hub: tasks of the stores this viewer sees, plus anything
 * assigned to them directly.
 */
function OpenTasks({
  heading,
  tasks,
  failed,
  stores,
  upn,
}: {
  heading: string;
  tasks: ServerTask[] | null;
  failed: boolean;
  stores: readonly Store[];
  upn: string;
}) {
  const storeById = useMemo(() => new Map(stores.map((st) => [Number(st.id), st])), [stores]);
  const mine = useMemo(() => {
    const me = upn.trim().toLowerCase();
    return (tasks ?? [])
      .filter((t) => storeById.has(t.storeId) || t.assignedTo.trim().toLowerCase() === me)
      .sort((a, b) => (a.dueDate || '9999').localeCompare(b.dueDate || '9999') || a.id - b.id);
  }, [tasks, storeById, upn]);
  // A page at a time (owner ruling 2026-10-29: no section over ~80% of the screen).
  const pager = usePager(mine, upn);
  return (
    <section className="dash-section">
      <div className="page-head">
        <h2 style={{ margin: 0 }}>{heading}</h2>
        <Link to="/tasks">All tasks →</Link>
      </div>
      {tasks === null ? (
        <p>Loading…</p>
      ) : failed ? (
        <p className="error">Couldn’t load your tasks — refresh to try again.</p>
      ) : mine.length === 0 ? (
        <p className="muted">No open tasks — nice work.</p>
      ) : (
        <>
          <PagerTop pager={pager} />
          {pager.items.map((t) => (
            <TaskRow
              key={t.id}
              task={t}
              store={storeById.get(t.storeId)}
              view="open"
              canManage={false}
              onReopen={() => undefined}
            />
          ))}
          <PagerBar pager={pager} noun="tasks" />
        </>
      )}
    </section>
  );
}
